import React, { useRef, useState, useEffect } from "react";
import "../styles/Packages.css";

const useVisible = (threshold = 0.08) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

const PACKAGES = [
  {
    slug: "starter",
    name: "Starter",
    tagline: "Get online fast.",
    price: "Starting at $300",
    note: "Perfect for individuals & small businesses.",
    featured: false,
    features: [
      "Up to 3 pages",
      "Responsive & Mobile-ready design",
      "Basic SEO setup",
      "SSL Certificate (HTTPS)",
      "FREE Domain & Hosting — 1 Year",
      "Contact form integration",
    ],
  },
  {
    slug: "portfolio",
    name: "Portfolio",
    tagline: "Showcase your work.",
    price: "Starting at $450",
    note: "Built for freelancers, creators & professionals.",
    featured: false,
    features: [
      "Up to 5 pages",
      "Custom portfolio & project showcase",
      "Responsive layout",
      "SEO-ready structure",
      "SSL Certificate",
      "FREE Domain & Hosting — 1 Year",
      "Social media links integration",
    ],
  },
  {
    slug: "business",
    name: "Business",
    tagline: "Grow your business.",
    price: "Starting at $750",
    note: "For growing businesses ready to convert visitors.",
    featured: true,
    features: [
      "Up to 5 pages",
      "Modern & professional UI design",
      "Advanced SEO optimisation",
      "Performance & Core Web Vitals optimisation",
      "SSL Certificate",
      "FREE Domain & Hosting — 1 Year",
      "1-Month FREE Maintenance & Updates",
      "Analytics & tracking integration",
    ],
  },
  {
    slug: "premium",
    name: "Premium",
    tagline: "Full digital presence.",
    price: "Starting at $1,300",
    note: "For brands that demand the best.",
    featured: false,
    features: [
      "Unlimited pages",
      "Advanced design & custom animations",
      "Full SEO strategy & implementation",
      "Performance & conversion optimisation",
      "SSL Certificate",
      "FREE Domain & Hosting — 1 Year",
      "1-Month FREE Maintenance & Updates",
      "Priority support channel",
      "A/B testing setup",
    ],
  },
];

function PackageCard({ pkg, index, visible }) {
  return (
    <div
      className={`pkg-card ${pkg.featured ? "pkg-card--featured" : ""} ${visible ? "pkg-card--in" : ""}`}
      style={{ transitionDelay: `${index * 110}ms` }}
    >
      {pkg.featured && (
        <div className="pkg-badge">Most Popular</div>
      )}

      <div className="pkg-top">
        <span className="pkg-tag">&lt;{pkg.name}/&gt;</span>
        <h3 className="pkg-name">{pkg.name}</h3>
        <p className="pkg-tagline">{pkg.tagline}</p>
      </div>

      <div className="pkg-price-wrap">
        <span className="pkg-price">{pkg.price}</span>
        <span className="pkg-note">{pkg.note}</span>
      </div>

      <ul className="pkg-features">
        {pkg.features.map((f) => (
          <li key={f}>
            <span className="pkg-check" aria-hidden="true">✔</span>
            {f}
          </li>
        ))}
      </ul>

      <a
        href={`https://wa.me/96181233235?text=Hello Codix, I'm interested in the ${pkg.name} package.`}
        target="_blank"
        rel="noopener noreferrer"
        className={pkg.featured ? "pkg-btn pkg-btn--featured" : "pkg-btn"}
      >
        Get Started
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </a>
    </div>
  );
}

function Packages() {
  const [headerRef, headerVis] = useVisible(0.1);
  const [gridRef, gridVis] = useVisible(0.05);

  return (
    <section id="packages" className="packages-section">
      <div className="about-rule" aria-hidden="true" />
      <div className="packages-container">

        {/* Header */}
        <div ref={headerRef} className={`pkg-header ${headerVis ? "pkg-header--in" : ""}`}>
          <span className="cdx-label">// 04 — pricing</span>
          <h2 className="cdx-title">
           &lt;<span className="orange"> our</span> Packages<span className="orange">/</span>&gt;
          </h2>
          <div className="cdx-rule" />
          <p className="pkg-subtitle">
            Transparent starting prices with no surprises. Every package is a foundation —
            customised to fit your exact business needs.
          </p>
        </div>

        {/* Grid */}
        <div ref={gridRef} className="pkg-grid">
          {PACKAGES.map((pkg, i) => (
            <PackageCard key={pkg.slug} pkg={pkg} index={i} visible={gridVis} />
          ))}
        </div>

        {/* Custom plan note */}
        <div className={`pkg-custom ${headerVis ? "pkg-custom--in" : ""}`}>
          <div className="pkg-custom-text">
            <span className="cdx-label" style={{ margin: 0 }}>// need something different?</span>
            <p>
              Not sure which package fits? We design <strong>custom plans</strong> tailored
              to your specific business, goals, and budget.
            </p>
          </div>
          <a
            href="https://book.codixlb.com"
            target="_blank"
            rel="noopener noreferrer"
            className="cdx-btn-primary"
          >
            Discuss a Custom Plan
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

export default Packages;